import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, extractError } from "../../lib/api";
import { useWS } from "../../context/WebSocketContext";
import { useAuth, canEditVouchers } from "../../context/AuthContext";
import { SectionCard } from "../../components/Ui";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { toast } from "sonner";
import { cn } from "../../lib/utils";
import { Pencil, Search } from "lucide-react";
import OutwardSettlementPanel, {
  FinalizedSalesSettlementReview,
  ProvisionalSettlementWizard,
} from "../../components/OutwardSettlementPanel";
import { SalesDispatchQueueCard } from "../../components/SalesSettlementChrome";

function FilterPills({ value, onChange, items, testId }) {
  return (
    <div className="flex flex-wrap gap-1 mt-2" data-testid={testId}>
      {items.map((it) => (
        <button
          key={it.value}
          type="button"
          onClick={() => onChange(it.value)}
          className={cn(
            "rounded-md border px-2.5 py-1 text-[11px] transition-colors",
            value === it.value
              ? "border-primary/40 bg-primary/10 text-foreground"
              : "border-border bg-card text-muted-foreground hover:bg-muted/40",
          )}
        >
          {it.label}
          {it.count != null ? ` (${it.count})` : ""}
        </button>
      ))}
    </div>
  );
}

function tallySynced(d) {
  return String(d.tally_sync_status || d.sales_voucher?.tally_sync_status || "").toUpperCase() === "SYNCED";
}

function matchesSearch(d, q) {
  if (!q) return true;
  const hay = [
    d.code,
    d.vehicle_number,
    d.gate_pass_code,
    d.party_name,
    d.sales_contract_number,
    d.sales_voucher_code,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return hay.includes(q);
}

/** Accounts → Sales settlement: same two-pane layout as purchase pending / voucher register. */
export default function AccountsSalesSettlementPanel({ hideDeskLink = false, initialDispatchId = null }) {
  const { user } = useAuth();
  const canEdit = canEditVouchers(user);
  const [data, setData] = useState({
    dispatches: [],
    sales_contracts: [],
    sales_voucher_register: [],
  });
  const [loading, setLoading] = useState(true);
  const [mainTab, setMainTab] = useState("pending");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedId, setSelectedId] = useState(null);
  const [search, setSearch] = useState("");
  const [provisionalPane, setProvisionalPane] = useState("final");

  const load = useCallback(() => {
    setLoading(true);
    api
      .get("/outward-dispatches/bootstrap", { params: { module: "voucher" } })
      .then((r) =>
        setData((prev) => ({
          dispatches: r.data?.dispatches || [],
          sales_contracts: r.data?.sales_contracts || [],
          sales_voucher_register: prev.sales_voucher_register || [],
          sales_voucher_register_count: r.data?.sales_voucher_register_count ?? prev.sales_voucher_register_count,
        })),
      )
      .catch((e) => toast.error(extractError(e)))
      .finally(() => setLoading(false));
  }, []);

  const loadFinalizedRegister = useCallback(() => {
    return api
      .get("/outward-dispatches/register", { params: { kind: "sales_voucher", limit: 100 } })
      .then((r) => {
        setData((prev) => ({
          ...prev,
          sales_voucher_register: r.data?.results || [],
          sales_voucher_register_count: r.data?.count ?? (r.data?.results || []).length,
        }));
      })
      .catch((e) => toast.error(extractError(e)));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!initialDispatchId || loading) return undefined;
    const id = Number(initialDispatchId);
    if (!Number.isFinite(id)) return undefined;
    const match = (data.dispatches || []).find((d) => d.id === id);
    if (!match) return undefined;
    setMainTab("pending");
    setStatusFilter("all");
    setSelectedId(id);
    if (
      match.sales_voucher_id
      && String(match.sales_settlement_status || "").toUpperCase() === "PROVISIONAL"
    ) {
      setProvisionalPane("final");
    }
    return undefined;
  }, [initialDispatchId, loading, data.dispatches]);

  const { subscribe } = useWS();
  useEffect(() => subscribe("outward_dispatch", load), [subscribe, load]);
  useEffect(() => subscribe("voucher", load), [subscribe, load]);

  useEffect(() => {
    if (mainTab !== "vouchers") return undefined;
    loadFinalizedRegister();
    return undefined;
  }, [mainTab, loadFinalizedRegister]);

  useEffect(() => {
    if (mainTab === "vouchers") return undefined;
    const t = window.setTimeout(() => loadFinalizedRegister(), 600);
    return () => window.clearTimeout(t);
  }, [mainTab, loadFinalizedRegister]);

  const buckets = useMemo(() => {
    const accountsPending = (data.dispatches || []).filter((d) => d.state === "ACCOUNTS_PENDING");
    const atDispatch = accountsPending.filter((d) => d.voucher_bucket === "dispatch" || !d.sales_voucher_id);
    const provisional = accountsPending.filter(
      (d) =>
        d.voucher_bucket === "provisional"
        || (d.sales_voucher_id && d.sales_settlement_status === "PROVISIONAL"),
    );
    const finalized = (data.sales_voucher_register || []).filter(
      (d) =>
        d.voucher_bucket === "finalized"
        || d.sales_settlement_status === "FINAL"
        || d.state === "COMPLETED",
    );
    return { atDispatch, provisional, finalized, pending: [...atDispatch, ...provisional] };
  }, [data.dispatches, data.sales_voucher_register]);

  const queue = useMemo(() => {
    if (mainTab === "pending") {
      if (statusFilter === "dispatch") return buckets.atDispatch;
      if (statusFilter === "provisional") return buckets.provisional;
      return buckets.pending;
    }
    if (statusFilter === "tally_pending") return buckets.finalized.filter((d) => !tallySynced(d));
    if (statusFilter === "tally_synced") return buckets.finalized.filter((d) => tallySynced(d));
    return buckets.finalized;
  }, [mainTab, statusFilter, buckets]);

  const filteredQueue = useMemo(() => {
    const q = search.trim().toLowerCase();
    return queue.filter((d) => matchesSearch(d, q));
  }, [queue, search]);

  useEffect(() => {
    if (!filteredQueue.length) {
      setSelectedId(null);
      return;
    }
    if (!filteredQueue.some((d) => d.id === selectedId)) {
      setSelectedId(filteredQueue[0].id);
      setProvisionalPane("final");
    }
  }, [filteredQueue, selectedId, mainTab, statusFilter]);

  const selected = useMemo(
    () => filteredQueue.find((d) => d.id === selectedId) || queue.find((d) => d.id === selectedId) || null,
    [filteredQueue, queue, selectedId],
  );

  const onSaved = useCallback(() => {
    load();
    if (mainTab === "vouchers" || selected?.sales_settlement_status === "FINAL") {
      loadFinalizedRegister();
    }
  }, [load, loadFinalizedRegister, mainTab, selected]);

  const reopenForEdit = useCallback(async () => {
    if (!selectedId) return;
    await api.post(`/outward-dispatches/${selectedId}/accounts-reopen`);
    toast.success("Reopened for editing — update drafts, then re-finalize");
    setMainTab("pending");
    setStatusFilter("provisional");
    await load();
  }, [selectedId, load]);

  const pendingPills = [
    { value: "all", label: "All", count: buckets.pending.length },
    { value: "dispatch", label: "At dispatch", count: buckets.atDispatch.length },
    { value: "provisional", label: "Provisional", count: buckets.provisional.length },
  ];
  const registerPills = [
    { value: "all", label: "Final settled", count: buckets.finalized.length },
    {
      value: "tally_pending",
      label: "Tally not synced",
      count: buckets.finalized.filter((d) => !tallySynced(d)).length,
    },
    {
      value: "tally_synced",
      label: "Tally synced",
      count: buckets.finalized.filter((d) => tallySynced(d)).length,
    },
  ];

  const selectedIsProvisional = Boolean(
    selected
    && selected.sales_voucher_id
    && String(selected.sales_settlement_status || "").toUpperCase() === "PROVISIONAL",
  );

  const renderWorkspace = () => {
    if (!selected) {
      return (
        <p className="text-sm text-muted-foreground py-12 text-center">
          Select one truck from the list to work on its sales voucher.
        </p>
      );
    }

    if (mainTab === "vouchers") {
      return (
        <FinalizedSalesSettlementReview
          key={`done-${selected.id}`}
          dispatch={selected}
          canEdit={canEdit}
          onEdit={canEdit ? reopenForEdit : undefined}
        />
      );
    }

    if (selectedIsProvisional) {
      return (
        <div className="space-y-3">
          {selected.accounts_refinalize_required || selected.accounts_reopen ? (
            <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/40 dark:border-amber-800 px-3 py-2 text-xs text-amber-950 dark:text-amber-100">
              Reopened for accounts edit
              {selected.accounts_reopen?.by ? ` by ${selected.accounts_reopen.by}` : ""}.
              Save draft steps, then re-finalize to update the same voucher numbers.
            </div>
          ) : null}
          {canEdit ? (
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                variant={provisionalPane === "dispatch" ? "default" : "outline"}
                className={provisionalPane === "dispatch" ? "bg-bansal-sage hover:bg-bansal-sageDark text-white" : ""}
                onClick={() => setProvisionalPane("dispatch")}
                data-testid="sales-edit-at-dispatch"
              >
                <Pencil className="h-3.5 w-3.5 mr-1" />
                Edit at-dispatch details
              </Button>
              <Button
                type="button"
                size="sm"
                variant={provisionalPane === "final" ? "default" : "outline"}
                className={provisionalPane === "final" ? "bg-bansal-sage hover:bg-bansal-sageDark text-white" : ""}
                onClick={() => setProvisionalPane("final")}
                data-testid="sales-edit-final-settlement"
              >
                Final settlement
              </Button>
            </div>
          ) : null}
          {provisionalPane === "dispatch" && canEdit ? (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">
                Change contracts, bags, provisional freight, and document details on the existing
                provisional voucher ({selected.sales_voucher_code || "SV"}). Does not create a new voucher.
              </p>
              <ProvisionalSettlementWizard
                key={`prov-edit-${selected.id}`}
                dispatch={selected}
                contracts={data.sales_contracts || []}
                onSaved={onSaved}
                forceEdit
              />
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">
                Party weight / lab / goods receipt and final sales{selected.freight_voucher_id ? " & freight" : ""}{" "}
                settlement. CD, brokerage, bardana, freight and GST stay editable until you post.
              </p>
              <OutwardSettlementPanel
                key={`final-${selected.id}`}
                dispatch={selected}
                onSaved={onSaved}
                mode="final"
              />
            </div>
          )}
        </div>
      );
    }

    return (
      <ProvisionalSettlementWizard
        key={`prov-${selected.id}`}
        dispatch={selected}
        contracts={data.sales_contracts || []}
        onSaved={onSaved}
      />
    );
  };

  return (
    <div className="space-y-4 min-w-0" data-testid="accounts-sales-settlement">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">
          Select one truck, complete its voucher, then move to the next — same pattern as purchase review.
        </p>
        {!hideDeskLink ? (
          <Button asChild variant="outline" size="sm">
            <Link to="/sales/vouchers">Open Sales Voucher desk</Link>
          </Button>
        ) : null}
      </div>

      <Tabs
        value={mainTab}
        onValueChange={(tab) => {
          setMainTab(tab);
          setSearch("");
          setSelectedId(null);
          setStatusFilter("all");
          setProvisionalPane("final");
        }}
        className="space-y-4"
      >
        <TabsList className="bg-card border border-border p-1 h-auto flex-wrap">
          <TabsTrigger value="pending" className="text-xs sm:text-sm">
            Pending review ({buckets.pending.length})
          </TabsTrigger>
          <TabsTrigger value="vouchers" className="text-xs sm:text-sm">
            Voucher register ({buckets.finalized.length})
          </TabsTrigger>
        </TabsList>

        {["pending", "vouchers"].map((tab) => (
          <TabsContent key={tab} value={tab} className="mt-0">
            <div
              className={cn(
                "grid grid-cols-1 gap-4 items-start min-w-0",
                "lg:grid-cols-[minmax(0,20rem)_minmax(0,1fr)]",
              )}
            >
              <div className="min-w-0 w-full lg:max-w-80 lg:sticky lg:top-20 self-start">
                <SectionCard
                  title={tab === "pending" ? "Awaiting review" : "Voucher register"}
                  testId={tab === "pending" ? "accounts-sales-pending-queue" : "accounts-sales-vouchers-queue"}
                  className="min-w-0"
                >
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                    <Input
                      className="pl-8 h-8 text-xs w-full"
                      placeholder="Vehicle, GP, party, contract, voucher…"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </div>
                  <FilterPills
                    value={statusFilter}
                    onChange={setStatusFilter}
                    items={tab === "pending" ? pendingPills : registerPills}
                    testId={`accounts-sales-${tab}-status-pills`}
                  />
                  {loading ? (
                    <p className="text-sm text-muted-foreground py-6 text-center">Loading…</p>
                  ) : filteredQueue.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-6 text-center">
                      {search.trim() ? "No matches." : "Nothing in this queue."}
                    </p>
                  ) : (
                    <div className="mt-3 space-y-1.5 max-h-[70vh] overflow-y-auto">
                      {filteredQueue.map((d) => (
                        <SalesDispatchQueueCard
                          key={d.id}
                          d={d}
                          selected={d.id === selectedId}
                          onSelect={() => setSelectedId(d.id)}
                          showVoucherActions={tab === "vouchers"}
                        />
                      ))}
                    </div>
                  )}
                </SectionCard>
              </div>

              <div className="min-w-0">
                <SectionCard
                  title={
                    selected
                      ? `Review · ${selected.sales_voucher_code || selected.code}`
                      : "Select a truck"
                  }
                  testId={`accounts-sales-${tab}-review`}
                  className="min-w-0"
                >
                  {renderWorkspace()}
                </SectionCard>
              </div>
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
